import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getServiceById } from '../services/api';
import { Users, FileCheck, CircleDollarSign, Clock, CalendarCheck, ScrollText, Phone, Globe, Building2 } from 'lucide-react';

function ServicePassportPage() {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        getServiceById(serviceId)
            .then(response => {
                setService(response.data);
            })
            .catch(err => {
                setError(err.response?.data?.message || 'Failed to load service details.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [serviceId]);

    

    const renderSection = (icon, title, content) => {
        if (!content || (Array.isArray(content) && content.length === 0)) {
            return (
                <div className="passport-section">
                    <h3>
                        {icon}
                        <span>{title}</span>
                    </h3>
                    <p>Not specified</p>
                </div>
            );
        }
        return (
            <div className="passport-section">
                <h3>
                    {icon}
                    <span>{title}</span>
                </h3>
                {Array.isArray(content) ? (
                    <ul>{content.map((item, index) => <li key={index}>{item}</li>)}</ul>
                ) : typeof content === 'string' && content.startsWith('http') ? (
                    <p><a href={content} target="_blank" rel="noopener noreferrer">{content}</a></p>
                ) : typeof content === 'string' ? (
                    <p dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }}></p>
                ) : (
                    <p>{content}</p>
                )}
            </div>
        );
    };

    if (loading) return <p className="loading-message">Loading service details...</p>;
    if (error) return <p className="error-message">Error: {error}</p>;
    if (!service) return <p>Service not found.</p>;

    return (
        <div className="service-passport-container">
            <div className="passport-main">
                <h1>{service.name}</h1>
                <p className="service-description">{service.description || 'No description provided.'}</p>
                <Link to={`/apply/${serviceId}`}>
                    <button className="start-application-button">Start Application</button>
                </Link>
                <hr className="passport-divider" />
                {renderSection(<Users size={18} />, "Who can Apply", service.whoCanApply?.filter(item => item !== 'Citizen' && item !== 'Local Company'))}
                {renderSection(<FileCheck size={18} />, "Eligibility Requirements", service.eligibilityRequirements)}
                {renderSection(<CircleDollarSign size={18} />, "Fee", service.fee)}
                {renderSection(<Clock size={18} />, "Processing Time", service.processingTime)}
                {renderSection(<CalendarCheck size={18} />, "Validity", service.validity)}
                {renderSection(<ScrollText size={18} />, "Legal References", service.legalReferences)}
            </div>
            <aside className="passport-sidebar">
                <div className="mda-info-box">
                    <img
                        src={service.mda?.logo || '/img/default-mda-logo.svg'}
                        alt={`${service.mda?.shortName || 'MDA'} Logo`}
                        className="mda-logo"
                        width='150'
                        onError={(e) => { e.target.onerror = null; e.target.src='/img/default-mda-logo.svg'; }}
                    />
                    <h3>{service.mda?.fullName || 'MDA Not Specified'}</h3>
                    {service.mda?.phone && (
                        <p className="mda-contact">
                            <Phone size={14} /> {service.mda.phone}
                        </p>
                    )}
                    {service.mda?.website && (
                        <p className="mda-contact">
                            <Globe size={14} />
                            <a href={service.mda.website} target="_blank" rel="noopener noreferrer">
                                {service.mda.website.replace(/^https?:\/\//, '')}
                            </a>
                        </p>
                    )}
                </div>
                <a href="https://portal.citizensupport.gov.zm/" target="_blank" rel="noopener noreferrer" className="support-button-link">
                    <button className="support-button">Citizen Support</button>
                </a>
                <div className="related-links">
                    <h4>You also might be interested in:</h4>
                    <ul>
                        <li><Link to="https://www.szi.gov.zm/">Smart Zambia Instititute</Link></li>
                        <li><Link to="https://www.mofnp.gov.zm/">Ministry of Finance & National Planning</Link></li>
                    </ul>
                </div>
            </aside>
        </div>
    );
}

export default ServicePassportPage;

